import { useMemo, useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import ChatMessage from "@/components/ChatMessage";
import { format } from "date-fns";

interface WhatsAppMessage {
  id: string;
  timestamp: number;
  sender: string;
  content: string;
  isSent: boolean;
  attachment?: any;
}

interface ChatData {
  id: string;
  file_name: string;
  messages: WhatsAppMessage[];
}

interface SyncedChatPanelProps {
  chat: ChatData;
  currentTime: number | null;
  searchQuery: string;
  onTimeSync: (timestamp: number) => void;
}

const SyncedChatPanel = ({ chat, currentTime, searchQuery, onTimeSync }: SyncedChatPanelProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);

  const filteredMessages = useMemo(() => {
    if (!searchQuery) return chat.messages;
    return chat.messages.filter(msg =>
      msg.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [chat.messages, searchQuery]);

  useEffect(() => {
    if (currentTime && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentTime]);

  const groupMessagesByDate = (messages: WhatsAppMessage[]) => {
    const groups: { [key: string]: WhatsAppMessage[] } = {};
    
    messages.forEach(msg => {
      const dateKey = format(new Date(msg.timestamp), "yyyy-MM-dd");
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(msg);
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(filteredMessages);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-4 border-b bg-card">
        <h3 className="font-semibold truncate">{chat.file_name}</h3>
        <p className="text-sm text-muted-foreground">
          {filteredMessages.length} messages
        </p>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-6">
          {Object.entries(messageGroups).map(([date, messages]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-4">
                <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                  {format(new Date(date), "MMMM d, yyyy")}
                </div>
              </div>

              {/* Messages for this date */}
              <div className="space-y-3">
                {messages.map((msg) => {
                  const isHighlighted = currentTime && Math.abs(msg.timestamp - currentTime) < 60000; // 1 minute tolerance
                  const isSearchMatch = searchQuery && msg.content.toLowerCase().includes(searchQuery.toLowerCase());

                  return (
                    <div
                      key={msg.id}
                      id={`msg-${chat.id}-${msg.timestamp}`}
                      ref={isHighlighted ? highlightedRef : null}
                      className={`transition-all ${
                        isHighlighted ? "ring-2 ring-primary ring-offset-2 rounded-lg" : ""
                      } ${isSearchMatch ? "bg-yellow-100 dark:bg-yellow-900/20" : ""}`}
                      onClick={() => onTimeSync(msg.timestamp)}
                    >
                      <ChatMessage
                        message={{
                          ...msg,
                          timestamp: new Date(msg.timestamp)
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SyncedChatPanel;
